import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Type-only import — sharing the canonical Workspace DTO type creates no
// runtime layer-order violation; the FSD rule's underlying concern is
// value imports (which would invert layer order at runtime).
// eslint-disable-next-line boundaries/element-types
import type { Workspace } from '@/entities/workspace';
import type { WorkspaceMenuHeaderAction } from '@/shared/types/workspace-menu.types';

export interface UseWorkspaceSelectorOptions {
  /** List of workspaces available for selection. */
  workspaces: Workspace[];
  /** Currently selected workspace ID. */
  selectedWorkspaceId: string | null;
  /** Organization display name (shown in popover header). */
  organizationName?: string;
  /** Organization logo URL (shown in popover header). */
  organizationLogoUrl?: string | null;
  /** Called when the user selects a workspace. */
  onWorkspaceChange?: (workspace: Workspace) => void;
  /** Optional header action (e.g. "Switch organization" link). */
  headerAction?: WorkspaceMenuHeaderAction;
  /** When true, the popover trigger is disabled and opening is blocked. */
  disabled?: boolean;
}

/**
 * Generates initials from an organization name.
 * Takes the first letter of the first two words, or the first two letters
 * if the name is a single word.
 */
function getOrganizationInitials(name: string): string {
  const words = name.trim().split(/\s+/);

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  if (words[0].length >= 2) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return words[0][0].toUpperCase();
}

/**
 * Encapsulates the non-visual logic for the workspace selector popover.
 *
 * Manages open/close state, search filtering, sorted workspace list,
 * current workspace lookup, and the selection callback. Extracted from
 * `WorkspaceMenu.tsx` so both the legacy menu and the upcoming
 * `SidebarRail` popover can share identical behaviour.
 */
export function useWorkspaceSelector({
  workspaces,
  selectedWorkspaceId,
  organizationName,
  organizationLogoUrl,
  onWorkspaceChange,
  headerAction,
  disabled = false,
}: UseWorkspaceSelectorOptions) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Derived data ──────────────────────────────────────────────────────

  const sortedWorkspaces = useMemo(
    () => [...workspaces].sort((a, b) => a.name.localeCompare(b.name)),
    [workspaces],
  );

  const filteredWorkspaces = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return sortedWorkspaces;
    return sortedWorkspaces.filter((ws) => ws.name.toLowerCase().includes(trimmed.toLowerCase()));
  }, [sortedWorkspaces, searchQuery]);

  const currentWorkspace = useMemo(
    () => workspaces.find((ws) => ws.id === selectedWorkspaceId) ?? null,
    [workspaces, selectedWorkspaceId],
  );

  const organizationInitials = useMemo(
    () => (organizationName ? getOrganizationInitials(organizationName) : null),
    [organizationName],
  );

  // ── Callbacks ─────────────────────────────────────────────────────────

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disabled && nextOpen) return;
      setOpen(nextOpen);
      if (!nextOpen) setSearchQuery('');
    },
    [disabled],
  );

  const handleSelect = useCallback(
    (workspace: Workspace) => {
      onWorkspaceChange?.(workspace);
      setOpen(false);
      setSearchQuery('');
    },
    [onWorkspaceChange],
  );

  const handleHeaderAction = useCallback(() => {
    if (!headerAction) return;

    if (headerAction.onClick) {
      headerAction.onClick();
      return;
    }

    if (headerAction.href) {
      const target = headerAction.target ?? '_self';
      if (target === '_blank') {
        window.open(headerAction.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.assign(headerAction.href);
      }
    }
  }, [headerAction]);

  const hasHeaderAction =
    typeof headerAction?.onClick === 'function' || Boolean(headerAction?.href);

  return {
    // Popover state
    open,
    handleOpenChange,

    // Search
    searchQuery,
    setSearchQuery,

    // Data
    sortedWorkspaces,
    filteredWorkspaces,
    currentWorkspace,

    // Organization display
    organizationName: organizationName ?? null,
    organizationLogoUrl: organizationLogoUrl ?? null,
    organizationInitials,

    // Actions
    handleSelect,
    handleHeaderAction,
    hasHeaderAction,
    headerActionTooltip: headerAction?.tooltip ?? t('components.workspaceMenu.switchOrganization'),

    // Trigger state
    disabled,
  } as const;
}
